import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet-async';
import { 
  Building2, User, Mail, Phone, FileText, Globe, MapPin, 
  Check, ArrowRight, Star, Zap, Crown, Diamond
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const tiers = [
  { name: 'Bronze', minTopup: 25000, discount: 10, icon: Star, color: 'from-amber-600 to-amber-800' },
  { name: 'Silver', minTopup: 50000, discount: 12.5, icon: Zap, color: 'from-gray-400 to-gray-600' },
  { name: 'Gold', minTopup: 75000, discount: 15, icon: Crown, color: 'from-yellow-500 to-yellow-700' },
  { name: 'Platinum', minTopup: 100000, discount: 18, icon: Diamond, color: 'from-purple-500 to-purple-700' },
];

const PartnerApply = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [partnerStatus, setPartnerStatus] = useState(null);
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    gst_number: '',
    address: '',
    website: '',
    business_description: ''
  });

  useEffect(() => {
    checkPartnerStatus();
  }, []);

  const checkPartnerStatus = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/partner/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPartnerStatus(response.data);
      
      // Pre-fill email from user if available
      const userResponse = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (userResponse.data) {
        setFormData(prev => ({
          ...prev,
          email: userResponse.data.email || '',
          contact_name: userResponse.data.name || ''
        }));
      }
    } catch (error) {
      console.error('Error checking partner status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please login to apply for partnership');
      navigate('/login', { state: { returnTo: '/partner/apply' } });
      return;
    }

    if (!formData.company_name || !formData.contact_name || !formData.email || !formData.phone) {
      toast.error('Please fill all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/partner/apply`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Application submitted successfully!');
      setPartnerStatus({ status: 'pending', is_partner: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fafafa]">
        <Header />
        <div className="max-w-7xl mx-auto px-6 py-20 flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full"></div>
        </div>
        <Footer />
      </div>
    );
  }

  // Already a partner - redirect to dashboard
  if (partnerStatus?.is_partner) {
    return (
      <div className="min-h-screen bg-[#fafafa]">
        <Header />
        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
            <Check className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="font-playfair text-3xl font-bold mb-4">You&apos;re Already a Partner!</h1>
          <p className="text-[#737373] mb-8">Access your partner dashboard to manage credits and view discounts.</p>
          <button
            onClick={() => navigate('/partner/dashboard')}
            className="px-8 py-4 bg-[#D4AF37] text-[#0d0d0d] font-manrope uppercase tracking-widest hover:bg-[#B8960F] transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  // Pending application
  if (partnerStatus?.status === 'pending') {
    return (
      <div className="min-h-screen bg-[#fafafa]">
        <Header />
        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <div className="w-20 h-20 mx-auto mb-6 bg-amber-100 rounded-full flex items-center justify-center">
            <FileText className="w-10 h-10 text-amber-600" />
          </div>
          <h1 className="font-playfair text-3xl font-bold mb-4">Application Under Review</h1>
          <p className="text-[#737373] mb-8">
            Your partner application is being reviewed. We&apos;ll notify you once it&apos;s approved.
            This usually takes 1-2 business days.
          </p>
          <button
            onClick={() => navigate('/marketplace')}
            className="px-8 py-4 border border-[#e5e5e5] text-[#1a1a1a] font-manrope uppercase tracking-widest hover:border-[#D4AF37] transition-colors"
          >
            Browse Marketplace
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid="partner-apply-page">
      <Helmet>
        <title>Become a Partner | BusinessMint PR</title>
        <meta name="description" content="Join our partner program and get up to 18% discount on all PR services. Apply now to become a BusinessMint PR partner." />
      </Helmet>
      <Header />

      {/* Hero Section */}
      <div className="bg-[#1a1a1a] text-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            Partner Program
          </h1>
          <p className="font-manrope text-lg md:text-xl text-gray-400 max-w-2xl mb-8">
            Join our partner network and unlock exclusive discounts on all PR services. 
            Perfect for agencies, consultants, and high-volume buyers.
          </p>
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-[#D4AF37]" />
              <span>Up to 18% Discount</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-[#D4AF37]" />
              <span>Prepaid Credits</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-[#D4AF37]" />
              <span>Priority Support</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tier Cards */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <h2 className="font-playfair text-2xl md:text-3xl font-bold text-center mb-4">Partner Tiers</h2>
        <p className="text-[#737373] text-center mb-12 max-w-2xl mx-auto">
          Your tier is determined by your topup amount. Higher topups unlock better discounts.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {tiers.map((tier) => {
            const TierIcon = tier.icon;
            return (
              <div key={tier.name} className="bg-white border border-[#e5e5e5] rounded-xl p-6 hover:border-[#D4AF37] transition-all">
                <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${tier.color} flex items-center justify-center mb-4`}>
                  <TierIcon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-playfair text-xl font-bold mb-2">{tier.name}</h3>
                <p className="text-3xl font-mono font-bold text-[#D4AF37] mb-1">{tier.discount}%</p>
                <p className="text-sm text-[#737373] mb-4">Discount on all services</p>
                <div className="pt-4 border-t border-[#e5e5e5]">
                  <p className="text-sm text-[#737373]">Min. Topup</p>
                  <p className="font-mono font-semibold">₹{tier.minTopup.toLocaleString('en-IN')}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Application Form */}
        <div className="max-w-2xl mx-auto">
          <div className="bg-white border border-[#e5e5e5] rounded-xl p-8">
            <h2 className="font-playfair text-2xl font-bold mb-6">Apply for Partnership</h2>
            
            {!localStorage.getItem('token') && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <p className="text-amber-800 text-sm">
                  Please <button onClick={() => navigate('/login', { state: { returnTo: '/partner/apply' } })} className="underline font-semibold">login</button> or{' '}
                  <button onClick={() => navigate('/register', { state: { returnTo: '/partner/apply' } })} className="underline font-semibold">register</button> to apply for partnership.
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-manrope text-[#737373] mb-2">
                    Company Name <span className="text-[#D4AF37]">*</span>
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#a3a3a3]" />
                    <input
                      type="text"
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      className="w-full pl-10 pr-4 py-3 border border-[#e5e5e5] focus:border-[#D4AF37] focus:outline-none font-manrope"
                      placeholder="Your Company Name"
                      data-testid="company-name-input"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-manrope text-[#737373] mb-2">
                    Contact Name <span className="text-[#D4AF37]">*</span>
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#a3a3a3]" />
                    <input
                      type="text"
                      value={formData.contact_name}
                      onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                      className="w-full pl-10 pr-4 py-3 border border-[#e5e5e5] focus:border-[#D4AF37] focus:outline-none font-manrope"
                      placeholder="Your Name"
                      data-testid="contact-name-input"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-manrope text-[#737373] mb-2">
                    Email <span className="text-[#D4AF37]">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#a3a3a3]" />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full pl-10 pr-4 py-3 border border-[#e5e5e5] focus:border-[#D4AF37] focus:outline-none font-manrope"
                      placeholder="email@company.com"
                      data-testid="email-input"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-manrope text-[#737373] mb-2">
                    Phone <span className="text-[#D4AF37]">*</span>
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#a3a3a3]" />
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full pl-10 pr-4 py-3 border border-[#e5e5e5] focus:border-[#D4AF37] focus:outline-none font-manrope"
                      placeholder="9876543210"
                      data-testid="phone-input"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-manrope text-[#737373] mb-2">GST Number</label>
                  <div className="relative">
                    <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#a3a3a3]" />
                    <input
                      type="text"
                      value={formData.gst_number}
                      onChange={(e) => setFormData({ ...formData, gst_number: e.target.value })}
                      className="w-full pl-10 pr-4 py-3 border border-[#e5e5e5] focus:border-[#D4AF37] focus:outline-none font-manrope"
                      placeholder="22AAAAA0000A1Z5"
                      data-testid="gst-input"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-manrope text-[#737373] mb-2">Website</label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#a3a3a3]" />
                    <input
                      type="url"
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      className="w-full pl-10 pr-4 py-3 border border-[#e5e5e5] focus:border-[#D4AF37] focus:outline-none font-manrope"
                      placeholder="https://yourcompany.com"
                      data-testid="website-input"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-manrope text-[#737373] mb-2">Business Address</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 w-5 h-5 text-[#a3a3a3]" />
                  <textarea
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border border-[#e5e5e5] focus:border-[#D4AF37] focus:outline-none font-manrope resize-none"
                    rows="2"
                    placeholder="Your business address"
                    data-testid="address-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-manrope text-[#737373] mb-2">
                  Tell us about your business
                </label>
                <textarea
                  value={formData.business_description}
                  onChange={(e) => setFormData({ ...formData, business_description: e.target.value })}
                  className="w-full px-4 py-3 border border-[#e5e5e5] focus:border-[#D4AF37] focus:outline-none font-manrope resize-none"
                  rows="3"
                  placeholder="What does your business do? How do you plan to use our services?"
                  data-testid="description-input"
                />
              </div>

              <button
                type="submit"
                disabled={submitting || !localStorage.getItem('token')}
                data-testid="submit-application-btn"
                className="w-full py-4 bg-[#D4AF37] text-[#0d0d0d] font-manrope uppercase tracking-widest hover:bg-[#B8960F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit Application
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PartnerApply;
